import React, { useRef, useState } from 'react';
import { toast } from 'sonner';
import { api, mensajeError } from '../../../lib/api';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Loader2 } from 'lucide-react';
import { BOTON_VERDE, FOCO, Interruptor, Pildoras } from './comun';

/**
 * Los ajustes del bot: modelo, esfuerzo, ritmo humano, el enlace de cita
 * (teléfono y texto), quién recibe el aviso de traspaso, con qué marca y
 * procedencia entra el lead de quien escribe primero por WhatsApp, el freno
 * de gasto (topes de respuestas y de llamadas al día) y los precios por
 * millón de tokens. Todo nace vacío en el servidor y lo rellena dirección:
 * aquí no hay ningún teléfono, nombre ni precio escrito.
 */

const OTRO = '__otro__';
const RE_MODELO = /^claude-[a-z0-9.-]+$/;
const LIMITE_TEXTO_CITA = 500;
const LIMITE_PROCEDENCIA = 80;
// Los valores por defecto del servidor, por si uno anterior no los manda.
const TOPES = [
  ['tope_respuestas_dia', 30, 'Respuestas por conversación en 24 h',
    'Como mucho, estas respuestas del bot en una misma conversación en 24 horas.'],
  ['tope_llamadas_dia', 500, 'Llamadas a Claude al día, en total',
    'Como mucho, estas llamadas a Claude al día entre todas las conversaciones.'],
];
export const VARIABLES_CITA = [
  ['{nombre}', 'el nombre del contacto'],
  ['{modalidad}', 'presencial u online'],
  ['{tratamiento}', 'lo que le interesa'],
];
const PRECIOS = [
  ['entrada', 'Entrada'],
  ['salida', 'Salida'],
  ['cache_lectura', 'Lectura de caché'],
  ['cache_escritura', 'Escritura en caché'],
];
const NOMBRE_ESFUERZO = { low: 'Bajo', medium: 'Medio', high: 'Alto' };

function precioATexto(v) {
  return v === null || v === undefined || v === '' ? '' : String(v).replace('.', ',');
}

function idDe(p) {
  return typeof p === 'string' ? p : p?.id;
}

/** El formulario a partir de lo que dice el servidor. */
export function formDe(bot) {
  const precios = bot.precios || {};
  return {
    modelo: bot.modelo || '',
    esfuerzo: bot.esfuerzo || 'low',
    ritmo_humano: bot.ritmo_humano !== false,
    telefono_cita: bot.telefono_cita || '',
    texto_cita: bot.texto_cita || '',
    avisar_a: (bot.avisar_a || []).map(idDe).filter(Boolean),
    avisar_whatsapp: bot.avisar_whatsapp || '',
    precios: Object.fromEntries(PRECIOS.map(([k]) => [k, precioATexto(precios[k])])),
    lead_marca: bot.lead_nuevo?.marca || '',
    lead_procedencia: bot.lead_nuevo?.procedencia || '',
    ...Object.fromEntries(TOPES.map(([k, defecto]) => [k, String(bot[k] ?? defecto)])),
  };
}

/** Quita espacios, «+», guiones y paréntesis: «+34 600 00 00 00» → «34600000000». */
export function soloCifras(v) {
  return String(v || '').replace(/[\s+\-().]/g, '');
}

/** El cuerpo del PUT /ajustes, o los errores que lo impiden. */
export function cuerpoAjustes(form) {
  const errores = {};
  const modelo = form.modelo.trim();
  if (!RE_MODELO.test(modelo)) {
    errores.modelo = 'Tiene que empezar por «claude-» y llevar solo minúsculas, cifras, puntos y guiones.';
  }
  const telefono = soloCifras(form.telefono_cita);
  if (telefono && !/^\d{6,20}$/.test(telefono)) {
    errores.telefono_cita = 'Solo cifras (de 6 a 20), con el prefijo del país.';
  }
  const avisarWa = soloCifras(form.avisar_whatsapp);
  if (avisarWa && !/^\d{6,20}$/.test(avisarWa)) {
    errores.avisar_whatsapp = 'Solo cifras (de 6 a 20), con el prefijo del país.';
  }
  const procedencia = form.lead_procedencia.replace(/\s+/g, ' ').trim();
  if (procedencia.length > LIMITE_PROCEDENCIA) {
    errores.lead_procedencia = `Como mucho ${LIMITE_PROCEDENCIA} caracteres.`;
  }
  const topes = {};
  TOPES.forEach(([k]) => {
    const t = String(form[k] ?? '').trim();
    if (!/^\d+$/.test(t) || Number(t) < 1) errores[k] = 'Un número entero, de 1 en adelante.';
    else topes[k] = Number(t);
  });
  if (form.texto_cita.length > LIMITE_TEXTO_CITA) {
    errores.texto_cita = `Como mucho ${LIMITE_TEXTO_CITA} caracteres.`;
  }
  const precios = {};
  PRECIOS.forEach(([k]) => {
    const t = (form.precios[k] || '').trim();
    if (!t) { precios[k] = null; return; }
    const n = Number(t.replace(',', '.'));
    if (!Number.isFinite(n) || n < 0) errores.precios = 'Los precios son números en euros (p. ej. 2,5), o vacíos.';
    else precios[k] = n;
  });
  if (Object.keys(errores).length) return { errores };
  return {
    cuerpo: {
      modelo,
      esfuerzo: form.esfuerzo,
      ritmo_humano: !!form.ritmo_humano,
      telefono_cita: telefono,
      texto_cita: form.texto_cita.trim(),
      avisar_a: form.avisar_a,
      avisar_whatsapp: avisarWa,
      precios,
      lead_nuevo: { marca: form.lead_marca, procedencia },
      ...topes,
    },
  };
}

function Campo({ id, etiqueta, ayuda, error, children }) {
  return (
    <div className="space-y-1 min-w-0">
      <label htmlFor={id} className="text-sm font-medium text-[#13231E]">{etiqueta}</label>
      {children}
      {error ? <p className="text-xs text-red-700" data-testid={`${id}-error`}>{error}</p> : null}
      {ayuda ? <p className="text-xs text-muted-foreground">{ayuda}</p> : null}
    </div>
  );
}

export default function AjustesBot({ bot, onCambio }) {
  const inicial = formDe(bot);
  const [form, setForm] = useState(inicial);
  const sugeridos = Array.isArray(bot.modelos_sugeridos) ? bot.modelos_sugeridos : [];
  const [otroModelo, setOtroModelo] = useState(!!inicial.modelo && !sugeridos.includes(inicial.modelo));
  const [errores, setErrores] = useState({});
  const [guardando, setGuardando] = useState(false);
  const textoCitaRef = useRef(null);
  const personas = Array.isArray(bot.personas_posibles) ? bot.personas_posibles : [];
  const marcas = (Array.isArray(bot.marcas_posibles) ? bot.marcas_posibles : []).filter((m) => m && m.clave);
  // Los que admite el servidor (si los manda); si no, los tres de siempre.
  const esfuerzos = (Array.isArray(bot.esfuerzos) && bot.esfuerzos.length ? bot.esfuerzos : Object.keys(NOMBRE_ESFUERZO))
    .map((k) => [k, NOMBRE_ESFUERZO[k] || k]);
  const cambiado = JSON.stringify(form) !== JSON.stringify(inicial);

  const poner = (campo, valor) => setForm((f) => ({ ...f, [campo]: valor }));

  const elegirModelo = (v) => {
    if (v === OTRO) { setOtroModelo(true); poner('modelo', ''); return; }
    setOtroModelo(false);
    poner('modelo', v);
  };

  const alternarPersona = (id) => setForm((f) => ({
    ...f,
    avisar_a: f.avisar_a.includes(id) ? f.avisar_a.filter((x) => x !== id) : [...f.avisar_a, id],
  }));

  // Mete la variable donde está el cursor (o al final).
  const meterVariable = (variable) => {
    const el = textoCitaRef.current;
    const actual = form.texto_cita;
    const ini = el && typeof el.selectionStart === 'number' ? el.selectionStart : actual.length;
    const fin = el && typeof el.selectionEnd === 'number' ? el.selectionEnd : actual.length;
    poner('texto_cita', `${actual.slice(0, ini)}${variable}${actual.slice(fin)}`);
    if (el) setTimeout(() => { el.focus(); el.setSelectionRange(ini + variable.length, ini + variable.length); }, 0);
  };

  const guardar = async (e) => {
    e?.preventDefault();
    if (guardando) return;
    const { cuerpo, errores: errs } = cuerpoAjustes(form);
    if (errs) { setErrores(errs); return; }
    setErrores({});
    setGuardando(true);
    try {
      const r = await api.put('/api/whatsapp/bot/ajustes', cuerpo);
      onCambio(r.data);
      toast.success('Ajustes del bot guardados');
    } catch (err) {
      toast.error(mensajeError(err, 'No se han podido guardar los ajustes'));
    } finally {
      setGuardando(false);
    }
  };

  const selectClase = `flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ${FOCO}`;

  return (
    <form onSubmit={guardar} className="space-y-5" data-testid="bot-ajustes" noValidate>
      <div className="grid gap-4 md:grid-cols-2">
        <Campo id="bot-modelo" etiqueta="Modelo" error={errores.modelo}
          ayuda="El que contesta a los contactos. Si no está en la lista, elige «Otro» y escríbelo tal cual lo da Anthropic.">
          {sugeridos.length ? (
            <select id="bot-modelo" value={otroModelo ? OTRO : form.modelo} className={selectClase}
              onChange={(e) => elegirModelo(e.target.value)} data-testid="bot-modelo">
              {sugeridos.map((m) => <option key={m} value={m}>{m}</option>)}
              <option value={OTRO}>Otro…</option>
            </select>
          ) : null}
          {otroModelo || !sugeridos.length ? (
            <Input id={sugeridos.length ? 'bot-modelo-otro' : 'bot-modelo'} value={form.modelo}
              aria-label={sugeridos.length ? 'Nombre del otro modelo' : undefined}
              onChange={(e) => poner('modelo', e.target.value)} placeholder="claude-…" spellCheck={false}
              autoComplete="off" className="mt-1.5" data-testid="bot-modelo-otro" />
          ) : null}
        </Campo>

        <div className="space-y-1">
          <span className="text-sm font-medium text-[#13231E]" id="bot-esfuerzo-titulo">Esfuerzo</span>
          <Pildoras etiqueta="Esfuerzo" opciones={esfuerzos} valor={form.esfuerzo}
            onCambio={(v) => poner('esfuerzo', v)} testid="bot-esfuerzo" />
          <p className="text-xs text-muted-foreground">
            Cuánto razona antes de contestar. «Bajo» contesta en pocos segundos; más esfuerzo tarda más y cuesta más.
          </p>
        </div>
      </div>

      <Interruptor id="bot-ritmo" activo={form.ritmo_humano} onCambio={(v) => poner('ritmo_humano', v)}
        titulo="Ritmo humano" testid="bot-ritmo">
        Antes de cada mensaje espera de 2 a 6 segundos según lo largo que sea, como si alguien lo escribiera.
        Apagado, contesta en cuanto tiene la respuesta.
      </Interruptor>

      <fieldset className="border-t pt-4 space-y-4">
        <legend className="text-sm font-semibold text-[#13231E] pr-2">Enlace para pedir cita</legend>
        <p className="text-xs text-muted-foreground -mt-2">
          Cuando el bot pasa a alguien al equipo le da un enlace de WhatsApp a este teléfono con el texto ya escrito.
        </p>
        <div className="grid gap-4 md:grid-cols-[minmax(0,16rem)_1fr]">
          <Campo id="bot-telefono-cita" etiqueta="Teléfono de cita" error={errores.telefono_cita}
            ayuda={form.telefono_cita ? 'Solo cifras, con el prefijo del país.' : undefined}>
            <Input id="bot-telefono-cita" inputMode="tel" autoComplete="off" value={form.telefono_cita}
              onChange={(e) => poner('telefono_cita', e.target.value)} placeholder="Cifras con prefijo, sin +"
              data-testid="bot-telefono-cita" />
            {!form.telefono_cita ? (
              <p className="text-xs text-amber-800" data-testid="bot-telefono-cita-falta">
                Falta: sin él, el bot no puede dar el enlace de cita.
              </p>
            ) : null}
          </Campo>
          <Campo id="bot-texto-cita" etiqueta="Texto de cita" error={errores.texto_cita}>
            <Textarea id="bot-texto-cita" ref={textoCitaRef} value={form.texto_cita} maxLength={LIMITE_TEXTO_CITA}
              onChange={(e) => poner('texto_cita', e.target.value)} rows={3} data-testid="bot-texto-cita"
              aria-describedby="bot-texto-cita-variables" />
            <div id="bot-texto-cita-variables" className="text-xs text-muted-foreground space-y-1">
              <div className="flex flex-wrap items-center gap-1.5">
                <span>Variables (pulsa para meterla):</span>
                {VARIABLES_CITA.map(([v, que]) => (
                  <button key={v} type="button" onClick={() => meterVariable(v)} title={`Meter ${v}: ${que}`}
                    className={`rounded border px-1.5 py-px font-mono text-[11px] hover:bg-muted/60 ${FOCO}`}
                    data-testid={`bot-variable-${v.slice(1, -1)}`}>
                    {v}
                  </button>
                ))}
                <span className="ml-auto tabular-nums">{form.texto_cita.length}/{LIMITE_TEXTO_CITA}</span>
              </div>
              <p>
                {VARIABLES_CITA.map(([v, que]) => `${v}: ${que}`).join('; ')}. Si el bot no sabe alguno, se deja fuera de la frase.
              </p>
            </div>
          </Campo>
        </div>
      </fieldset>

      <fieldset className="border-t pt-4 space-y-3">
        <legend className="text-sm font-semibold text-[#13231E] pr-2">Avisos de traspaso</legend>
        <p className="text-xs text-muted-foreground -mt-1">
          Cuando el bot pasa una conversación al equipo, avisa con el resumen del lead.
        </p>
        <p className="text-xs text-muted-foreground" data-testid="bot-avisar-a-ayuda">
          {form.avisar_a.length
            ? 'El aviso de la campana llega SOLO a las personas marcadas.'
            : 'Sin nadie marcado, el aviso de la campana llega como los demás avisos: a todo su rol (dirección y administradores).'}
        </p>
        <div>
          <div className="text-sm font-medium text-[#13231E] mb-1" id="bot-avisar-a-titulo">Aviso en la campana de ClinicPilot</div>
          {personas.length ? (
            <div className="grid gap-1 sm:grid-cols-2" role="group" aria-labelledby="bot-avisar-a-titulo">
              {personas.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm rounded px-1 py-0.5 hover:bg-muted/40">
                  <input type="checkbox" checked={form.avisar_a.includes(p.id)} onChange={() => alternarPersona(p.id)}
                    data-testid={`bot-avisar-${p.id}`} />
                  <span>{p.nombre}</span>
                  {p.rol ? <span className="text-xs text-muted-foreground">· {p.rol}</span> : null}
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">No hay nadie con acceso a la bandeja a quien avisar.</p>
          )}
        </div>
        <Campo id="bot-avisar-whatsapp" etiqueta="WhatsApp de avisos" error={errores.avisar_whatsapp}
          ayuda="Recibe el resumen por WhatsApp. Vacío: no se avisa por WhatsApp. Fuera de las 24 h se manda con la plantilla «Aviso de traspaso».">
          <Input id="bot-avisar-whatsapp" inputMode="tel" autoComplete="off" value={form.avisar_whatsapp}
            onChange={(e) => poner('avisar_whatsapp', e.target.value)} placeholder="Cifras con prefijo, sin +"
            className="max-w-xs" data-testid="bot-avisar-whatsapp" />
        </Campo>
      </fieldset>

      <fieldset className="border-t pt-4 space-y-3" data-testid="bot-lead-nuevo">
        <legend className="text-sm font-semibold text-[#13231E] pr-2">Leads nuevos de quien escribe primero por WhatsApp</legend>
        <p className="text-xs text-muted-foreground -mt-1">
          Si escribe al número del bot alguien que no es lead, el bot (cuando ya envía) le crea su lead con esta marca y
          esta procedencia.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <Campo id="bot-lead-marca" etiqueta="Marca">
            <select id="bot-lead-marca" value={form.lead_marca} className={selectClase}
              onChange={(e) => poner('lead_marca', e.target.value)} data-testid="bot-lead-marca">
              <option value="">Ninguna: no se crean leads</option>
              {marcas.map((m) => <option key={m.clave} value={m.clave}>{m.nombre || m.clave}</option>)}
              {form.lead_marca && !marcas.some((m) => m.clave === form.lead_marca)
                ? <option value={form.lead_marca}>{form.lead_marca} (ya no está en Configuración)</option> : null}
            </select>
            {!form.lead_marca ? (
              <p className="text-xs text-amber-800" data-testid="bot-lead-sin-marca">
                Sin marca, no se crea el lead de quien escribe primero: la conversación se queda solo en la bandeja.
              </p>
            ) : null}
          </Campo>
          <Campo id="bot-lead-procedencia" etiqueta="Procedencia" error={errores.lead_procedencia}
            ayuda={`Texto corto, como se verá en Leads (${form.lead_procedencia.length}/${LIMITE_PROCEDENCIA}).`}>
            <Input id="bot-lead-procedencia" value={form.lead_procedencia} maxLength={LIMITE_PROCEDENCIA}
              autoComplete="off" onChange={(e) => poner('lead_procedencia', e.target.value)}
              data-testid="bot-lead-procedencia" />
          </Campo>
        </div>
        <p className="text-xs text-muted-foreground" data-testid="bot-lead-nota">
          Para que esas conversaciones reciban seguimientos, marca también esa marca en Plantillas → Marcas.
        </p>
      </fieldset>

      <fieldset className="border-t pt-4 space-y-2" data-testid="bot-freno">
        <legend className="text-sm font-semibold text-[#13231E] pr-2">Freno de gasto</legend>
        <p className="text-xs text-muted-foreground -mt-1">
          Topes para que una conversación que no acaba o un fallo no disparen el coste. Al llegar a uno, el bot deja de
          llamar a Claude (en esa conversación, o en todas) hasta que pasa el plazo.
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          {TOPES.map(([k, , etiqueta, ayuda]) => (
            <Campo key={k} id={`bot-${k}`} etiqueta={etiqueta} error={errores[k]} ayuda={ayuda}>
              <Input id={`bot-${k}`} type="number" inputMode="numeric" min={1} step={1} value={form[k]}
                onChange={(e) => poner(k, e.target.value)} className="max-w-[10rem]" data-testid={`bot-${k}`} />
            </Campo>
          ))}
        </div>
      </fieldset>

      <fieldset className="border-t pt-4 space-y-2">
        <legend className="text-sm font-semibold text-[#13231E] pr-2">Precios por millón de tokens (opcional)</legend>
        <p className="text-xs text-muted-foreground -mt-1">
          En euros, según la tarifa del modelo elegido. Sirven para calcular el coste en las métricas; vacíos, se
          enseñan tokens.
        </p>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {PRECIOS.map(([k, nombre]) => (
            <div key={k} className="space-y-1">
              <label htmlFor={`bot-precio-${k}`} className="text-xs text-muted-foreground">{nombre} (€)</label>
              <Input id={`bot-precio-${k}`} inputMode="decimal" autoComplete="off" value={form.precios[k]}
                onChange={(e) => setForm((f) => ({ ...f, precios: { ...f.precios, [k]: e.target.value } }))}
                data-testid={`bot-precio-${k}`} />
            </div>
          ))}
        </div>
        {errores.precios ? <p className="text-xs text-red-700" data-testid="bot-precios-error">{errores.precios}</p> : null}
      </fieldset>

      <div className="flex flex-wrap items-center gap-2 border-t pt-4">
        <Button type="submit" disabled={guardando || !cambiado} className={BOTON_VERDE} data-testid="bot-ajustes-guardar">
          {guardando ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> : null}
          Guardar los ajustes
        </Button>
        {cambiado ? (
          <Button type="button" variant="ghost" onClick={() => { setForm(inicial); setErrores({}); setOtroModelo(!!inicial.modelo && !sugeridos.includes(inicial.modelo)); }}
            data-testid="bot-ajustes-deshacer">
            Deshacer los cambios
          </Button>
        ) : <span className="text-xs text-muted-foreground">Sin cambios.</span>}
      </div>
    </form>
  );
}
